import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IDashboardDetails, IDashboardInitialState } from "./dashboardModel";
import {
  getAllDashboardOpportunities,
  getClientsProspectLeadList,
  getDashboard,
  getDashboardInner,
  getDashboardUpcomingFollowUp,
  getInquiryClosingAmount,
  getLeadClosingAmount,
  getUpcomingCallsList,
  getUpcomingMeetingsList,
  getUpcomingTasksList,
} from "./services/dashboard.services";

const initialState: IDashboardInitialState = {
  loading: false,
  formLoading: false,
  error: "",
  dateFilter: {
    SelectedDate: null,
    FromDate: null,
    ToDate: null,
  },
  followupDateFilter: {
    SelectedDate: null,
    FromDate: null,
    ToDate: null,
  },
  DashboardDetails: {
    TotalInquiry: null,
    OpenNBInquiry: null,
    OwnInquiry: null,
    GroupCode: "",
    ProspectCount: "",
    OpportunityCount: null,
    TodaysCalls: null,
    TodaysMeetings: null,
    TodaysTasks: null,
    UpComingCalls: null,
    UpComingMeetings: null,
    UpComingTasks: null,
    MissedCalls: null,
    MissedMeetings: null,
    MissedTasks: null,
    InquiryTypeWiseCounts: [],
    LeadStatusWiseCount: [],
    EmployeeWiseCounts: [],
    EmployeeWiseProspectsCounts: [],
    EmployeeWiseInquiryCloseAmount: [],
    EmployeeWiseLeadCloseAmount: [],
  },
  DashboardUpcomingFollowUpDetails: {},
  DashboardInnerList: [],
  ClientsProspectLeadList: [],
  UpcomingTasksList: [],
  UpcomingMeetingsList: [],
  UpcomingCallsList: [],
  UpComingCallsTab: 0,
  UpComingMeetingsTab: 0,
  UpComingTasksTab: 0,
  InquiryClosingAmountList: [],
  LeadClosingAmountList: [],
  DashboardOpportunityList: [],
  ClosingAmountTab: 0,
  FilterCustomDashboardInner: {
    InquiryNo: "",
    InquiryDate: "",
    GroupCode: "",
    LeadName: "",
    InquiryType: "",
    InquirySubType: "",
    Frequency: "",
    LeadType: "",
    LeadStatus: "",
    InquiryAllotmentName: "",
    CoPersonAllotmentName: "",
  },
};

export const DashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    clearDashboardDetails: (state) => {
      state.DashboardDetails = initialState.DashboardDetails;
    },
    clearDashboardUpcomingFollowUpDetails: (state) => {
      state.DashboardUpcomingFollowUpDetails =
        initialState.DashboardUpcomingFollowUpDetails;
    },
    clearDateFilter: (state) => {
      state.dateFilter = initialState.dateFilter;
    },
    setDateFilter: (state, action) => {
      state.dateFilter = action.payload;
    },
    clearFollowupDateFilter: (state) => {
      state.followupDateFilter = initialState.followupDateFilter;
    },
    setFollowupDateFilter: (state, action) => {
      state.followupDateFilter = action.payload;
    },
    setUpComingCallsTab: (state, action) => {
      state.UpComingCallsTab = action.payload;
    },
    setUpComingMeetingsTab: (state, action) => {
      state.UpComingMeetingsTab = action.payload;
    },
    setUpComingTasksTab: (state, action) => {
      state.UpComingTasksTab = action.payload;
    },
    setClosingAmountTab: (state, action) => {
      state.ClosingAmountTab = action.payload;
    },
    setFilterCustomDashboardInner: (state, action) => {
      state.FilterCustomDashboardInner = action.payload;
    },
    clearFilterCustomDashboardInner: (state) => {
      state.FilterCustomDashboardInner =
        initialState.FilterCustomDashboardInner;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getDashboard.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getDashboard.fulfilled,
      (state, action: PayloadAction<IDashboardDetails>) => {
        state.loading = false;
        state.DashboardDetails = action.payload;
      },
    );
    builder.addCase(getDashboard.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.DashboardDetails = initialState.DashboardDetails;
    });

    builder.addCase(getDashboardUpcomingFollowUp.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getDashboardUpcomingFollowUp.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.DashboardUpcomingFollowUpDetails = action.payload;
      },
    );
    builder.addCase(getDashboardUpcomingFollowUp.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.DashboardUpcomingFollowUpDetails =
        initialState.DashboardUpcomingFollowUpDetails;
    });

    builder.addCase(getDashboardInner.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getDashboardInner.fulfilled,
      (state, action: PayloadAction<[]>) => {
        state.loading = false;
        state.DashboardInnerList = action.payload;
      },
    );
    builder.addCase(getDashboardInner.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.DashboardInnerList = initialState.DashboardInnerList;
    });

    builder.addCase(getClientsProspectLeadList.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getClientsProspectLeadList.fulfilled,
      (state, action: PayloadAction<[]>) => {
        state.loading = false;
        state.ClientsProspectLeadList = action.payload;
      },
    );
    builder.addCase(getClientsProspectLeadList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.ClientsProspectLeadList = initialState.ClientsProspectLeadList;
    });

    // Activity API
    builder.addCase(getUpcomingTasksList.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getUpcomingTasksList.fulfilled,
      (state, action: PayloadAction<[]>) => {
        state.loading = false;
        state.UpcomingTasksList = action.payload;
      },
    );
    builder.addCase(getUpcomingTasksList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.UpcomingTasksList = initialState.UpcomingTasksList;
    });

    builder.addCase(getUpcomingCallsList.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getUpcomingCallsList.fulfilled,
      (state, action: PayloadAction<[]>) => {
        state.loading = false;
        state.UpcomingCallsList = action.payload;
      },
    );
    builder.addCase(getUpcomingCallsList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.UpcomingCallsList = initialState.UpcomingCallsList;
    });

    builder.addCase(getUpcomingMeetingsList.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getUpcomingMeetingsList.fulfilled,
      (state, action: PayloadAction<[]>) => {
        state.loading = false;
        state.UpcomingMeetingsList = action.payload;
      },
    );
    builder.addCase(getUpcomingMeetingsList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.UpcomingMeetingsList = initialState.UpcomingMeetingsList;
    });

    builder.addCase(getInquiryClosingAmount.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getInquiryClosingAmount.fulfilled,
      (state, action: PayloadAction<[]>) => {
        state.loading = false;
        state.InquiryClosingAmountList = action.payload;
      },
    );
    builder.addCase(getInquiryClosingAmount.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.InquiryClosingAmountList = initialState.InquiryClosingAmountList;
    });
    builder.addCase(getLeadClosingAmount.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getLeadClosingAmount.fulfilled,
      (state, action: PayloadAction<[]>) => {
        state.loading = false;
        state.LeadClosingAmountList = action.payload;
      },
    );
    builder.addCase(getLeadClosingAmount.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.LeadClosingAmountList = initialState.LeadClosingAmountList;
    });

    builder.addCase(getAllDashboardOpportunities.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getAllDashboardOpportunities.fulfilled,
      (state, action: PayloadAction<[]>) => {
        state.loading = false;
        state.DashboardOpportunityList = action.payload;
      },
    );
    builder.addCase(getAllDashboardOpportunities.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.DashboardOpportunityList = initialState.DashboardOpportunityList;
    });
  },
});

export const {
  clearDashboardDetails,
  clearDashboardUpcomingFollowUpDetails,
  clearDateFilter,
  setDateFilter,
  clearFollowupDateFilter,
  setFollowupDateFilter,
  setUpComingCallsTab,
  setUpComingMeetingsTab,
  setUpComingTasksTab,
  setClosingAmountTab,
  clearFilterCustomDashboardInner,
  setFilterCustomDashboardInner,
} = DashboardSlice.actions;
export default DashboardSlice.reducer;
